import _ from 'lodash';
import moment from 'moment-timezone';
import { DEBOUNCE } from '@/core/core.constants';
import {
  sqCapsuleGroupStore,
  sqInvestigateStore,
  sqManualConditionStore,
  sqTrendCapsuleStore,
  sqTrendStore,
} from '@/core/core.stores';
import { flux } from '@/core/flux.module';
import { PUSH_IGNORE } from '@/core/flux.service';
import { setPluginCapsuleRegion } from '@/plugin/plugin.actions';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import {
  conditionFormula,
  makeCapsulesUnique,
  removeInvalidCapsules,
} from '@/tools/manualCondition/conditionFormula.service';
import {
  cancelPreviewCapsules,
  generatePreviewConditionFromCapsules,
  removePreviewCapsules,
  removeTrendSelectedRegion,
  setEditModeForCondition,
  exposedForTesting as trendActionsExposedForTesting,
} from '@/trendData/trend.actions';
import { asyncDebounce } from '@/utilities/asyncDebounce';
import { formatApiError } from '@/utilities/utilities';
import { sqConditionsApi } from '@seeqdev/api/endpoint/ConditionsApi';
import type { CapsuleGroupCapsule } from '@/tools/manualCondition/manualCondition.constants';
import type { Capsule } from '@/utilities/datetime.constants';

export function setIsExecuting(isExecuting: boolean) {
  flux.dispatch('MANUAL_CONDITION_SET_IS_EXECUTING', { isExecuting }, PUSH_IGNORE);
}

/**
 * Requests capsule properties for the capsules in the manual condition store that have a propertiesSource
 * property. A call is made for each condition that encompases the range of all the capsules. The returned
 * capsules are then matched and the properties are copied
 *
 * @return {Promise} a promise that resolves with the formula
 */
export function fetchPropertiesAndBuildFormula() {
  const groupCapsules: CapsuleGroupCapsule[] = sqCapsuleGroupStore.capsules;

  return Promise.resolve()
    .then(() =>
      _.chain(groupCapsules)
        .filter((c) => !_.isNil(c.propertiesSource))
        .groupBy((c) => (c.propertiesSource as NonNullable<typeof c.propertiesSource>).conditionId)
        .mapValues((capsules) => ({
          capsules,
          startTime: _.minBy(capsules, (c) => c.propertiesSource?.startTime)?.propertiesSource?.startTime as number,
          endTime: _.maxBy(capsules, (c) => c.propertiesSource?.endTime)?.propertiesSource?.endTime as number,
        }))
        .map(({ capsules, startTime, endTime }, conditionId) =>
          sqConditionsApi
            .getCapsules({
              id: conditionId,
              start: moment(startTime).toISOString(),
              end: moment(endTime).toISOString(),
            })
            .catch((error) => Promise.reject(formatApiError(error)))
            .then(({ data }) =>
              _.map(capsules, ({ id, startTime, endTime, propertiesSource }) => {
                const properties = _.get(
                  _.find(
                    data.capsules as any[],
                    (responseCapsule) => responseCapsule.id === propertiesSource?.capsuleId,
                  ),
                  'properties',
                  [],
                );

                return { id, startTime, endTime, properties } as Capsule;
              }),
            ),
        )
        .thru((p) => Promise.all(p))
        .value(),
    )
    .then((r) =>
      _.chain(r)
        .flatten()
        .concat(_.filter(groupCapsules, (c) => _.isNil(c.propertiesSource)) as Capsule[])
        .value(),
    )
    .then((c) => _.tap(c, exposedForTesting.setCapsules))
    .then(removeInvalidCapsules)
    .then(makeCapsulesUnique)
    .then(conditionFormula);
}

export function setColor(color: string): void {
  flux.dispatch('MANUAL_CONDITION_SET_COLOR', { color });
  triggerManualConditionPreviewDebounced();
}

export const triggerManualConditionPreviewDebounced = asyncDebounce(triggerManualConditionPreview, DEBOUNCE.PREVIEW);

export async function triggerManualConditionPreview() {
  await cancelPreviewCapsules();
  if (sqInvestigateStore.activeTool === TREND_TOOLS.MANUAL_CONDITION) {
    if (_.isNil(sqTrendCapsuleStore.editingId)) {
      setEditModeForCondition(sqManualConditionStore.id);
    }

    await generatePreviewCondition();
  } else {
    removePreviewCapsules();
  }
}

const generatePreviewCondition = () => {
  const capsules = sqCapsuleGroupStore.capsules;
  const conditionId = sqManualConditionStore.id;
  const color = sqManualConditionStore.color;
  return generatePreviewConditionFromCapsules(conditionId, capsules, color);
};

/**
 * Sets the capsules in the capsule group store. If the Manual Condition tool is open, this also kicks off the
 * preview for manual condition.
 *
 * @param {Object} capsules - array of capsule objects
 */
export function setCapsules(capsules: CapsuleGroupCapsule[]) {
  flux.dispatch('CAPSULE_GROUP_SET_CAPSULES', { capsules });
  triggerManualConditionPreviewDebounced();
}

/**
 * Adds the capsule represented by the selection to the capsule group store and removes the selection. If the Manual
 * Condition tool is open, this also kicks off the preview for manual condition.
 */
export function pickSelectedRegion({ formCapsule }: { formCapsule?: CapsuleGroupCapsule }) {
  if (_.includes(sqTrendStore.allSelectedRegionOptionsPlugins, sqInvestigateStore.activeTool)) {
    trendActionsExposedForTesting.setShowSelectedRegionOptions(!sqTrendStore.showSelectedRegionOptions);
    setPluginCapsuleRegion(sqTrendStore.selectedRegion);
  } else {
    const capsule = formCapsule || {
      startTime: sqTrendStore.selectedRegion.min,
      endTime: sqTrendStore.selectedRegion.max,
      properties: [],
    };

    flux.dispatch('CAPSULE_GROUP_ADD_CAPSULE', { capsule });
    removeTrendSelectedRegion();
    triggerManualConditionPreviewDebounced();
  }
}

export const exposedForTesting = {
  setCapsules,
};
